/* eslint-disable complexity */
import { deburr } from 'lodash';
import { IntlShape } from 'react-intl';

import {
  DateRangeFilter,
  FilterColumnDefinition,
  RangeFilter,
  SelectFilter,
  SortableColumn,
  SortConfig,
  SortDirection,
  SORT_LABEL_MESSAGE_IDS,
  TextMatchFilter
} from './list-filter-types';

/**
 * Checks if a string contains a search term (case-insensitive, diacritics-insensitive).
 */
export const containsTerm = (value: string, term: string): boolean => {
  return deburr(value.toLowerCase()).indexOf(deburr(term.toLowerCase())) !== -1;
};

/**
 * Filters a list by a numeric range (from/to inclusive).
 * Returns items where `accessor(item)` falls within [from, to].
 * If both from and to are undefined, all items pass.
 */
export const applyRangeFilter = <T>(
  items: T[],
  accessor: (item: T) => number | undefined,
  filter: RangeFilter | undefined
): T[] => {
  if (!filter || (filter.from === undefined && filter.to === undefined)) {
    return items;
  }

  return items.filter(item => {
    const value = accessor(item);
    if (value === undefined) {
      return false;
    }
    if (filter.from !== undefined && value < filter.from) {
      return false;
    }
    if (filter.to !== undefined && value > filter.to) {
      return false;
    }
    return true;
  });
};

/**
 * Filters a list by a date range (from/to inclusive).
 * Returns items where `accessor(item)` falls within [from, to].
 * If both from and to are undefined, all items pass.
 */
export const applyDateRangeFilter = <T>(
  items: T[],
  accessor: (item: T) => Date | undefined,
  filter: DateRangeFilter | undefined
): T[] => {
  if (!filter || (filter.from === undefined && filter.to === undefined)) {
    return items;
  }

  return items.filter(item => {
    const value = accessor(item);
    if (!value) {
      return false;
    }
    const time = value.getTime();
    if (filter.from !== undefined && time < filter.from.getTime()) {
      return false;
    }
    if (filter.to !== undefined && time > filter.to.getTime()) {
      return false;
    }
    return true;
  });
};

/**
 * Filters a list by text matching against selected values.
 * If selectedValues is empty, all items pass.
 */
export const applyTextMatchFilter = <T>(
  items: T[],
  accessor: (item: T) => string,
  filter: TextMatchFilter | undefined
): T[] => {
  if (!filter || filter.selectedValues.length === 0) {
    return items;
  }

  const selectedSet = new Set(filter.selectedValues);
  return items.filter(item => selectedSet.has(accessor(item)));
};

/**
 * Filters a list by a select filter (checkbox-style).
 * Returns items where the field value is selected (true) in the selectedValues map.
 * If no values are deselected (all true), all items pass.
 */
export const applySelectFilter = <T>(
  items: T[],
  accessor: (item: T) => string,
  filter: SelectFilter | undefined
): T[] => {
  if (!filter) {
    return items;
  }

  const hasDeselected = Object.values(filter.selectedValues).some(v => !v);
  if (!hasDeselected) {
    return items;
  }

  return items.filter(item => {
    const value = accessor(item);
    return filter.selectedValues[value] === true;
  });
};

/**
 * Filters a list by a free-text search term.
 * Matches against one or more string fields (case-insensitive, diacritics-insensitive).
 * If the search term is empty, all items pass.
 */
export const applySearch = <T>(items: T[], accessors: ((item: T) => string)[], searchTerm: string): T[] => {
  const trimmed = searchTerm.trim();
  if (trimmed.length === 0) {
    return items;
  }

  return items.filter(item => accessors.some(accessor => containsTerm(accessor(item), trimmed)));
};

/**
 * Sorts items by a column, given a comparison type.
 * Returns a new array (does not mutate).
 */
export const applySort = <T>(
  items: T[],
  sortConfig: SortConfig | undefined,
  comparators: Record<string, (a: T, b: T) => number>
): T[] => {
  if (!sortConfig) {
    return items;
  }

  const comparator = comparators[sortConfig.column];
  if (!comparator) {
    return items;
  }

  const sorted = [...items].sort(comparator);
  return sortConfig.direction === SortDirection.DESC ? sorted.reverse() : sorted;
};

/**
 * Counts the number of active filters across range, date range, text match, and select filters.
 */
export const countActiveFilters = (filters: {
  rangeFilters?: Record<string, RangeFilter | undefined>;
  dateRangeFilters?: Record<string, DateRangeFilter | undefined>;
  textMatchFilters?: Record<string, TextMatchFilter | undefined>;
  selectFilters?: Record<string, SelectFilter | undefined>;
}): number => {
  let count = 0;

  if (filters.rangeFilters) {
    for (const filter of Object.values(filters.rangeFilters)) {
      if (filter && (filter.from !== undefined || filter.to !== undefined)) {
        count += 1;
      }
    }
  }

  if (filters.dateRangeFilters) {
    for (const filter of Object.values(filters.dateRangeFilters)) {
      if (filter && (filter.from !== undefined || filter.to !== undefined)) {
        count += 1;
      }
    }
  }

  if (filters.textMatchFilters) {
    for (const filter of Object.values(filters.textMatchFilters)) {
      if (filter && filter.selectedValues.length > 0) {
        count += 1;
      }
    }
  }

  if (filters.selectFilters) {
    for (const filter of Object.values(filters.selectFilters)) {
      if (filter) {
        const hasDeselected = Object.values(filter.selectedValues).some(v => !v);
        if (hasDeselected) {
          count += 1;
        }
      }
    }
  }

  return count;
};

export interface SortOption {
  value: string;
  label: string;
}

/**
 * Builds sort dropdown options from sortable column definitions.
 * Each column produces two options (asc + desc) with translated labels
 * using ICU MessageFormat templates with `{columnName}` interpolation.
 */
export const buildSortOptions = (columns: SortableColumn[], intl: IntlShape): SortOption[] => {
  const options: SortOption[] = [];

  for (const column of columns) {
    /* eslint-disable formatjs/enforce-default-message */
    const columnName = intl.formatMessage({
      id: column.columnNameId,
      defaultMessage: column.columnNameDefaultMessage
    });
    /* eslint-enable formatjs/enforce-default-message */

    const labelMessages = SORT_LABEL_MESSAGE_IDS[column.labelStyle];

    options.push({
      value: `${column.key}:${SortDirection.ASC}`,
      label: intl.formatMessage(labelMessages[SortDirection.ASC], { columnName })
    });

    options.push({
      value: `${column.key}:${SortDirection.DESC}`,
      label: intl.formatMessage(labelMessages[SortDirection.DESC], { columnName })
    });
  }

  return options;
};

/**
 * Parses a sort option value string (e.g. "total_amount_due:desc")
 * back into a SortConfig.
 */
export const parseSortOptionValue = (value: string): SortConfig => {
  const lastColonIndex = value.lastIndexOf(':');
  const column = value.substring(0, lastColonIndex);
  const direction = value.substring(lastColonIndex + 1) as SortDirection;
  return { column, direction };
};

/**
 * Converts a SortConfig into a sort option value string.
 */
export const toSortOptionValue = (config: SortConfig): string => {
  return `${config.column}:${config.direction}`;
};

// ---------------------------------------------------------------------------
// Filter field config builder
// ---------------------------------------------------------------------------

interface FilterFieldConfigLabels {
  showAllLabel: string;
  filterByRangeLabel: string;
  fromLabel: string;
  toLabel: string;
}

/**
 * Builds the sidebar's FilterFieldConfig[] from declarative column definitions.
 * Replaces per-feature boilerplate that manually maps each filter field.
 */
export const buildFilterFieldConfigs = (
  columns: FilterColumnDefinition[],
  draftFilters: Record<string, unknown>,
  activeFilterKeys: Set<string>,
  intl: IntlShape,
  labels: FilterFieldConfigLabels
) => {
  return columns.map(column => {
    // eslint-disable-next-line formatjs/enforce-default-message
    const label = intl.formatMessage({ id: column.labelId, defaultMessage: column.labelDefaultMessage });

    switch (column.type) {
      case 'textMatch': {
        const filter = draftFilters[column.key] as { selectedValues: string[] } | undefined;
        return {
          type: 'textMatch' as const,
          key: column.key,
          label,
          options: column.options,
          selectedValues: filter?.selectedValues || []
        };
      }
      case 'range': {
        const filter = draftFilters[column.key] as { from?: number; to?: number } | undefined;
        return {
          type: 'range' as const,
          key: column.key,
          label,
          isActive: activeFilterKeys.has(column.key),
          from: filter?.from?.toString(),
          to: filter?.to?.toString(),
          currencySymbol: column.currencySymbol,
          ...labels
        };
      }
      case 'dateRange': {
        const filter = draftFilters[column.key] as { from?: Date; to?: Date } | undefined;
        const formatDate = column.formatDateForInput;
        return {
          type: 'dateRange' as const,
          key: column.key,
          label,
          isActive: activeFilterKeys.has(column.key),
          fromValue: filter?.from && formatDate ? formatDate(filter.from) : undefined,
          toValue: filter?.to && formatDate ? formatDate(filter.to) : undefined,
          ...labels
        };
      }
      case 'select': {
        const filter = draftFilters[column.key] as { selectedValues: Record<string, boolean> } | undefined;
        return {
          type: 'select' as const,
          key: column.key,
          label,
          options: column.options,
          selectedValues: filter?.selectedValues || {}
        };
      }
      default:
        return undefined;
    }
  }).filter(Boolean);
};
